import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import React from "react";

import { cloneDeep } from "lodash";
import { createContext, useContext, useEffect, useMemo } from "react";
import ReactFlow, {
  Controls,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import CustomEdgeComponent from "./reactFlowComponents/CustomEdgeComponent";
import ColorSelectorNodeWithValue from "./reactFlowComponents/CustomNodeComponentWithValue";
import ColorSelectorNode from "./reactFlowComponents/CustomNodeComponents";
import Logo from "./reactFlowComponents/Logo";
import StickyNoteNode from "./reactFlowComponents/StickyNoteNode";
import { useTranslation } from "react-i18next";
import TradingGraph from "@/components/charts/TradingGraph";
import { useGetCheckForex } from "@/api/strategyApi";
const FlowContext = createContext<{ edges: any[]; nodes: any[] } | undefined>(
  undefined
);

const useFlowContext = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error("useFlowContext must be used within a FlowProvider");
  }
  return context;
};

const FlowProvider = ({
  children,
  edges,
  nodes,
}: {
  children: React.ReactNode;
  edges: any[];
  nodes: any[];
}) => {
  return (
    <FlowContext.Provider value={{ edges, nodes }}>
      {children}
    </FlowContext.Provider>
  );
};

const edgeTypes1 = {
  buttonedge: CustomEdgeComponent,
};

const nodeTypes1 = {
  selectorNode: ColorSelectorNode,
  stickyNoteNode: StickyNoteNode,
};
const nodeTypes2 = {
  selectorNode: ColorSelectorNodeWithValue,
  stickyNoteNode: StickyNoteNode,
};

const ViewModalProvider = ({
  isOpen,
  onRequestClose,
  selected,
  showValues = false,
  isForex = false,
}: any) => {
  const canvas = useReactFlow();
  const { t } = useTranslation("common");

  const _edges = useMemo(
    () =>
      selected?.edges?.filter(Boolean).map((e: any) => {
        let ed = cloneDeep(e);
        ed.type = "buttonedge";
        ed.markerEnd = {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: "#64748B",
        };
        ed.data = {
          startLabel: "start edge label",
          endLabel: "end edge label",
        };
        return ed;
      }),
    [selected.edges]
  );
  useEffect(() => {
    let val = true;
    const _toId = setTimeout(() => {
      canvas?.fitView({ duration: 800 });
      val = false;
    }, 200);
    return () => {
      if (val) clearTimeout(_toId);
    };
  }, [canvas]);
  return (
    <>
      <FlowProvider edges={_edges} nodes={selected?.nodes}>
        <Dialog
          open={isOpen}
          onOpenChange={() => {
            onRequestClose();
          }}
        >
          <DialogContent className="max-w-max">
            <DialogHeader>
              <DialogTitle>{t("Preview")}</DialogTitle>
            </DialogHeader>
            <div
              id="react-flow-container-share"
              style={{
                width: "100%",
                height: "calc(100vh - 300px)",
                overflow: "hidden",
              }}
              className="relative"
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[4%] text-accent">
                <div className="flex flex-col justify-between items-center h-full"></div>
                <div className="subTitle"></div>
              </div>
              <div className="absolute z-[-2] bottom-0 text-accent left-0 right-0">
                {false && (
                  <div className="flex flex-col justify-between items-center h-full">
                    <Logo className="w-[600px] max-h-[220px] px-24 text-accent " />
                  </div>
                )}
                <div className="subTitle"></div>
              </div>
              <ReactFlow
                nodes={selected?.nodes}
                edges={_edges}
                nodeTypes={showValues ? nodeTypes2 : nodeTypes1}
                edgeTypes={edgeTypes1}
                style={{ zIndex: "revert", overflow: "hidden" }}
                className="main__diagram"
                fitViewOptions={{ duration: 800 }}
                minZoom={0.03}
                onLoad={() => {
                  canvas.fitView({ duration: 800 });
                }}
                defaultEdgeOptions={{ animated: true }}
              >
                <Controls className="!bottom-12" />
              </ReactFlow>
              {selected.symbol && (
                <div className="absolute top-0 left-0 w-full h-full z-[-1]">
                  <TradingGraph
                    symbol={selected?.symbol}
                    timeframe={selected?.timeframe}
                    disableInteraction
                    canvas_data={JSON.stringify(selected?.nodes)}
                  ></TradingGraph>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant={"outline"} onClick={() => onRequestClose()}>
                {t("Close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </FlowProvider>
    </>
  );
};

const ViewModal = (props: any) => {
  const { data: response } = useGetCheckForex(props?.selected?.symbol);
  const isForex = response?.data; // ✅ ensure you extract the boolean
  return (
    <ReactFlowProvider>
      <ForexContext.Provider value={{ isForex }}>
        <ViewModalProvider {...props} />
      </ForexContext.Provider>
    </ReactFlowProvider>
  );
};
export default ViewModal;
export { useFlowContext };

export const ForexContext = createContext<
  { isForex: boolean | undefined } | undefined
>(undefined);

export const useForex = () => {
  const context = useContext(ForexContext);
  if (!context) throw new Error("useForex must be used within a ForexProvider");
  return context;
};
